/**
 * @file 发送上报
 * @author dafo<huanghouin@baijiahulian.com>
 * @module util/request
 */
import { toQuery, parse } from './json';
import delay from './delay';
import storage from './storage';
import { Config, extendCommonField, isReady, onReady, publicParams } from './config';
import xhr from './xhr';

/**
 * 由于不同类型的日志需要上报给不同的域名(gsxtj或者qingzhou)，在此动态获取获取上报的域名
 *
 * @param {Object} jsonOrString 上报参数对象, 沿用原有的变量命名
 * @returns {Array.<string>} 上报域名数组
 */
function getRequestDomains() {
  var jsonOrString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!jsonOrString) {
    return [Config.DOMAIN];
  }
  // TODO 暂时 fmp/rmp perf.gif 接口只上报到大数据，仅作分析对比，后续要去掉
  if (jsonOrString.only_big_data) {
    return [Config.DOMAIN];
  }
  if (['page_load'].includes(jsonOrString.req_type)) {
    return [Config.DOMAIN, Config.QINGZHOU_DOMAIN];
  }
  // 这块的逻辑
  var reqTypes = ['api_call', 'log', 'fmp', 'rmp'];
  if (reqTypes.includes(jsonOrString.req_type) || jsonOrString.metric) {
    return [Config.QINGZHOU_DOMAIN];
  }
  return [Config.DOMAIN];
}

/**
 * 发送上报请求
 *
 * @function request.post
 * @param {string} url 上报地址
 * @param {Object} jsonOrString 键值对对象或字符串
 * @param {Function=} callback 上报后的回调
 * @param {Array=} data post 的数据
 */
export var post = function post(url) {
  var jsonOrString = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var callback = arguments.length > 2 ? arguments[2] : undefined;
  var data = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  var domains = [];
  if (url.indexOf('?') > -1) {
    // 命中了日志重发机制，注意处理不能重复上报相同的参数，造成异常日志
    // 比如url=time.gif?url=http%3A%2F%2Ftest-m.gaotu.cn%2F&uuid=xxx
    var query = url.split('?')[1];
    var queryObj = parse(query);
    domains = getRequestDomains(queryObj);
  } else {
    domains = getRequestDomains(jsonOrString);

    // 带有abtest等公共参数，目前只发送给哈勃大数据服务，不发送到轻舟（除了perf.gif）
    if (domains.includes(Config.DOMAIN)) {
      Object.assign(jsonOrString, publicParams);
    }
    // 这里的 type 是上报类型对应的地址，比如 view 事件，对应的默认就是 pv0.gif 。
    var _query = toQuery(extendCommonField(jsonOrString, {
      type: url
    }));
    if (_query) {
      url += "?".concat(_query);
    }
    storage.save(url, data);
    storage.pending(url);
  }

  // 静态文件是否通过file协议
  var isFileProtocol = document && document.location.protocol === 'file:';
  domains.forEach(function (domain) {
    // 通过file协议访问时添加https协议
    var targetURL = isFileProtocol ? "https:".concat(domain).concat(url) : domain + url;
    var isSuccess = data.length ? navigator.sendBeacon(targetURL, JSON.stringify(data)) : navigator.sendBeacon(targetURL);
    if (isSuccess && navigator.onLine) {
      storage.remove(url, data);
      storage.resend(post);
    } else {
      storage.deletePending(url);
    }
    callback && callback();
  });
};
var delayPost = delay(post);

/**
 * 发送上报请求
 *
 * @function request.get
 * @param {string} url 上报地址
 * @param {Object} jsonOrString 键值对对象或字符串
 * @param {Function=} callback 上报后的回调
 */
export var get = function get(url) {
  var jsonOrString = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var callback = arguments.length > 2 ? arguments[2] : undefined;
  var domains = [];
  if (url.indexOf('?') > -1) {
    // 命中了日志重发机制，注意处理不能重复上报相同的参数，造成异常日志
    // 比如url=time.gif?url=http%3A%2F%2Ftest-m.gaotu.cn%2F&uuid=xxx
    var query = url.split('?')[1];
    var queryObj = parse(query);
    domains = getRequestDomains(queryObj);
  } else {
    if (!jsonOrString) {
      jsonOrString = {};
    }
    domains = getRequestDomains(jsonOrString);

    // 带有abtest等公共参数，目前只发送给哈勃大数据服务，不发送到轻舟（除了perf.gif）
    if (domains.includes(Config.DOMAIN)) {
      Object.assign(jsonOrString, publicParams);
    }
    var _query2 = toQuery(extendCommonField(jsonOrString, {
      type: url
    }));
    if (_query2) {
      url += "?".concat(_query2);
    }
    storage.save(url);
  }

  // 静态文件是否通过file协议
  var isFileProtocol = document && document.location.protocol === 'file:';
  domains.forEach(function (domain) {
    // 通过file协议访问时添加https协议
    var targetURL = isFileProtocol ? "https:".concat(domain).concat(url) : domain + url;
    xhr(targetURL, function (isSuccess) {
      if (isSuccess) {
        storage.remove(url);
        storage.resend(get);
      } else {
        storage.deletePending(url);
      }
      callback && callback();
    });
    storage.pending(url);
  });
};
var delayGet = delay(get);

/**
 * 发送上报请求
 *
 * @param {string=} verb HTTP Verb
 * @param {boolean} isRunAtOnce 是否立即上报
 * @return {Function} 选择到的上报提交方法
 */
export default function request(verb, isRunAtOnce) {
  var isImmediateSend = isRunAtOnce || !Config.DELAY;
  var requester = String(verb).toLowerCase() === 'post' && navigator.sendBeacon ? isImmediateSend ? post : delayPost : isImmediateSend ? get : delayGet;
  if (isReady()) {
    return requester;
  }
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return onReady(function () {
      return requester.apply(void 0, args);
    });
  };
}

/**
 * 简单实现发起请求的能力的封装，仅供内部使用,直接用第三方库比如axios 会使包体积过大，同时代码编译也有问题
 *
 * @param {Object} options 发送请求的参数
 * @return  {Promise<Object>}
 */
export var ajax = function ajax(options) {
  return new Promise(function (resolve, reject) {
    var _options$url = options.url,
      url = _options$url === void 0 ? '' : _options$url,
      _options$method = options.method,
      method = _options$method === void 0 ? 'POST' : _options$method,
      _options$headers = options.headers,
      headers = _options$headers === void 0 ? {} : _options$headers,
      _options$data = options.data,
      data = _options$data === void 0 ? {} : _options$data;
    if (url) {
      var _xhr = new XMLHttpRequest();
      _xhr.open(method.toUpperCase(), url);
      var contentType = headers['content-type'] || headers['Content-Type'];
      var sendData = '';
      if (!contentType) {
        headers['content-type'] = 'application/json';
      }
      Object.keys(headers).forEach(function (key) {
        _xhr.setRequestHeader(key.toLowerCase(), headers[key]);
      });
      if (headers['content-type'] === 'application/json') {
        sendData = JSON.stringify(data);
      }
      if (headers['content-type'] === 'application/x-www-form-urlencoded') {
        sendData = Object.keys(data).map(function (key) {
          return "".concat(encodeURIComponent(key), "=").concat(encodeURIComponent(data[key]));
        });
      }
      _xhr.addEventListener('load', function (e) {
        if (_xhr.readyState === XMLHttpRequest.DONE || _xhr.readyState === XMLHttpRequest.prototype.DONE) {
          var resData = {};
          if (_xhr.status === 200 && _xhr.responseText) {
            try {
              resData = JSON.parse(_xhr.responseText);
              resolve(resData);
            } catch (e) {
              // 后期补充其他类型的返回值
            }
          } else {
            reject(_xhr.responseText);
          }
        }
      });

      // 其他情况暂时不处理
      _xhr.send(sendData);
    }
  });
};